import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, AlertCircle, Briefcase } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * SmartHireCTA — A context-aware CTA button that adapts behaviour based on auth state.
 *
 * - Not logged in        → links to /signup/company
 * - Logged in as company → links to /company/matches (dashboard)
 * - Logged in as engineer → shows a toast explaining the situation
 *
 * Props:
 *   label      — Button text (default: "Start hiring")
 *   className  — Extra classes
 *   showIcon   — Whether to show the ArrowRight icon (default: true)
 */
export default function SmartHireCTA({ label = "Start hiring", className = "", showIcon = true }) {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [showToast, setShowToast] = useState(false);

    function handleClick(e) {
        if (!isAuthenticated) return; // let the NavLink do its thing

        e.preventDefault();

        if (user?.role === "company") {
            navigate("/company/matches");
        } else {
            // engineer or other role
            setShowToast(true);
            setTimeout(() => setShowToast(false), 6000);
        }
    }

    return (
        <>
            <NavLink
                to="/signup/company"
                className={className}
                onClick={handleClick}
            >
                {isAuthenticated && user?.role === "company" ? (
                    <>
                        <Briefcase size={16} /> Dashboard
                    </>
                ) : (
                    <>
                        {label} {showIcon && <ArrowRight size={16} />}
                    </>
                )}
            </NavLink>

            {/* Toast notification for engineers */}
            <AnimatePresence>
                {showToast && (
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 28 }}
                        style={{
                            position: "fixed",
                            bottom: "2rem",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 9999,
                            width: "min(420px, calc(100vw - 2rem))",
                        }}
                    >
                        <div
                            style={{
                                background: "rgba(15, 10, 40, 0.95)",
                                backdropFilter: "blur(20px)",
                                border: "1px solid rgba(139, 92, 246, 0.3)",
                                borderRadius: "16px",
                                padding: "16px 20px",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "12px",
                                boxShadow: "0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(139,92,246,0.1)",
                            }}
                        >
                            <div
                                style={{
                                    background: "rgba(139, 92, 246, 0.15)",
                                    borderRadius: "10px",
                                    padding: "8px",
                                    flexShrink: 0,
                                }}
                            >
                                <AlertCircle size={18} style={{ color: "rgb(167, 139, 250)" }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: "white", fontSize: "14px" }}>
                                    You're signed in as an Engineer
                                </div>
                                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", marginTop: "4px", lineHeight: 1.4 }}>
                                    Want to hire instead?{" "}
                                    <NavLink
                                        to="/signup/company"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowToast(false);
                                        }}
                                        style={{
                                            color: "rgb(167, 139, 250)",
                                            textDecoration: "underline",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Create a company account
                                    </NavLink>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowToast(false)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "rgba(255,255,255,0.4)",
                                    cursor: "pointer",
                                    padding: "2px",
                                    flexShrink: 0,
                                }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
